/**
 * Loading of the Zoom Contact Center SDK `<script>` tag.
 *
 * The tag is injected at app startup (see `main.tsx`) rather than when the
 * equipment check mounts. When the SDK starts video it re-loads this page
 * into a hidden iframe — its "peer window" — sets `ZCB_IS_TOP_LEVEL` on it
 * and waits for the SDK inside that copy to answer a postMessage handshake.
 * That copy boots on step 1, so a step-3 injection never happens there, the
 * handshake is never answered, and the SDK only abandons it after 60
 * seconds of retries before falling back to in-page video.
 */

import { resolveZoomConfig } from './zoomConfig';

const SCRIPT_ID = 'zoom-video-client';

/** Inject the SDK script once per document, returning the existing tag if present. */
export function ensureZoomSdkScript(): HTMLScriptElement {
  const existing = document.getElementById(SCRIPT_ID);
  if (existing) {
    return existing as HTMLScriptElement;
  }

  const script = document.createElement('script');
  script.id = SCRIPT_ID;
  script.src = resolveZoomConfig().sdkUrl;
  document.body.appendChild(script);
  return script;
}

/** True once the SDK has executed and exposed its entry point on `window`. */
export function isZoomSdkReady(): boolean {
  return typeof (window as unknown as { VideoClient?: unknown }).VideoClient === 'function';
}
